import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// SPDX-License-Identifier: AGPL-3.0-or-later
import { useId } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { ExtensionUrl, findExtension, } from '@intakeform/fhir';
import { Input } from '../primitives/Input.js';
import { Label } from '../primitives/Label.js';
import { Stack } from '../primitives/Stack.js';
import { Button } from '../primitives/Button.js';
/** ISO 3166-1 alpha-2 codes the address-bias dropdown offers by default. */
const ADDRESS_COUNTRY_OPTIONS = [
    { code: '', label: 'No country bias' },
    { code: 'US', label: 'United States' },
    { code: 'CA', label: 'Canada' },
    { code: 'GB', label: 'United Kingdom' },
    { code: 'AU', label: 'Australia' },
    { code: 'NZ', label: 'New Zealand' },
    { code: 'IE', label: 'Ireland' },
    { code: 'IN', label: 'India' },
];
function isAddressItem(item) {
    if (item.type !== 'string')
        return false;
    return findExtension(item, ExtensionUrl.address)?.valueBoolean === true;
}
/** Read the current country bias from the item's extensions. */
function readCountryBias(item) {
    return findExtension(item, ExtensionUrl.addressCountry)?.valueCode ?? '';
}
/** Replace the country-bias extension. Empty string removes it. */
function writeCountryBias(item, code) {
    const others = (item.extension ?? []).filter((e) => e.url !== ExtensionUrl.addressCountry);
    const next = code.length === 0 ? others : [...others, { url: ExtensionUrl.addressCountry, valueCode: code }];
    return { ...item, extension: next };
}
/**
 * Item types that can serve as an enableWhen gate. The renderer
 * projects choice-field draft values (AnswerOption objects) and the
 * gate's `answerCoding` / `answerString` to a comparable primitive
 * before comparing, so choice gates work live (see bead 442 +
 * packages/ui/src/renderer/enableWhen.ts).
 */
const ELIGIBLE_GATE_TYPES = [
    'boolean',
    'integer',
    'decimal',
    'date',
    'dateTime',
    'time',
    'string',
    'text',
    'choice',
];
const ENABLE_WHEN_OPERATORS = [
    { value: 'exists', label: 'has a value' },
    { value: '=', label: 'equals' },
    { value: '!=', label: 'does not equal' },
    { value: '>', label: 'greater than' },
    { value: '>=', label: 'greater than or equal to' },
    { value: '<', label: 'less than' },
    { value: '<=', label: 'less than or equal to' },
];
/**
 * Linearise the questionnaire tree in document order (pre-order DFS).
 * The standard `walkQuestionnaireItems` walker yields in reverse-stack
 * order which would put later siblings before earlier ones — we need
 * source-code order so the "items before me" filter is intuitive.
 */
function flattenItemsInDocumentOrder(items) {
    const out = [];
    for (const item of items) {
        out.push(item);
        if (item.item !== undefined)
            out.push(...flattenItemsInDocumentOrder(item.item));
    }
    return out;
}
/** Items that appear before `currentLinkId` and can serve as a gate. */
function getEligibleGates(allItems, currentLinkId) {
    const flat = flattenItemsInDocumentOrder(allItems);
    const out = [];
    for (const item of flat) {
        if (item.linkId === currentLinkId)
            break;
        if (ELIGIBLE_GATE_TYPES.includes(item.type))
            out.push(item);
    }
    return out;
}
/**
 * Map a gate item's `type` to the matching `answer*` key on EnableWhen.
 * Returns the FHIR-canonical key (e.g. `boolean` → `answerBoolean`).
 * For `choice` the key is option-dependent — the caller resolves it
 * via {@link answerKeyForChoiceOption}.
 */
function answerKeyForType(type) {
    switch (type) {
        case 'boolean':
            return 'answerBoolean';
        case 'integer':
            return 'answerInteger';
        case 'decimal':
            return 'answerDecimal';
        case 'date':
            return 'answerDate';
        case 'dateTime':
            return 'answerDateTime';
        case 'time':
            return 'answerTime';
        case 'string':
        case 'text':
        default:
            return 'answerString';
    }
}
function answerKeyForChoiceOption(option) {
    if (option.valueCoding !== undefined && typeof option.valueCoding.code === 'string') {
        const v = { code: option.valueCoding.code };
        if (option.valueCoding.system !== undefined)
            v.system = option.valueCoding.system;
        if (option.valueCoding.display !== undefined)
            v.display = option.valueCoding.display;
        return { key: 'answerCoding', value: v };
    }
    if (typeof option.valueString === 'string') {
        return { key: 'answerString', value: option.valueString };
    }
    if (typeof option.valueInteger === 'number') {
        return { key: 'answerInteger', value: option.valueInteger };
    }
    return null;
}
/**
 * Stable key for an answerOption, mirrors the renderer's
 * `answerOptionKey` — used as the <option value=""> in the
 * choice-gate value picker.
 */
function optionKey(option) {
    if (option.valueCoding !== undefined) {
        return `c:${option.valueCoding.system ?? ''}|${option.valueCoding.code ?? ''}`;
    }
    if (option.valueString !== undefined)
        return `s:${option.valueString}`;
    if (option.valueInteger !== undefined)
        return `i:${String(option.valueInteger)}`;
    return '';
}
function optionLabel(option) {
    if (option.valueCoding !== undefined) {
        return option.valueCoding.display ?? option.valueCoding.code ?? '';
    }
    if (option.valueString !== undefined)
        return option.valueString;
    if (option.valueInteger !== undefined)
        return String(option.valueInteger);
    return '';
}
/**
 * Strip every `answer*` key from a clause, leaving question + operator.
 * Used when the user picks a new source item type — the previous answer
 * key would have been wrong for the new type.
 */
function clearAnswerValue(clause) {
    const next = { question: clause.question, operator: clause.operator };
    return next;
}
/**
 * Build a starter clause for a freshly-added condition row. Defaults to
 * the first eligible gate item, `exists` operator (the safest default —
 * works for every type without prompting for a value).
 */
function makeStarterClause(firstGate) {
    return {
        question: firstGate?.linkId ?? '',
        operator: 'exists',
        answerBoolean: true,
    };
}
function getHelpText(item) {
    const ext = item.extension?.find((e) => e.url === ExtensionUrl.helpText);
    return ext?.valueString || '';
}
function setHelpText(item, value) {
    const nextExtensions = (item.extension || []).filter((e) => e.url !== ExtensionUrl.helpText);
    if (value.trim().length > 0) {
        nextExtensions.push({ url: ExtensionUrl.helpText, valueString: value });
    }
    return {
        ...item,
        extension: nextExtensions.length > 0 ? nextExtensions : undefined,
    };
}
const EMPTY_SOURCE = { kind: 'none', field: '', system: '', code: '' };
/** Field options exposed for kind=patient. Matches the demographics convention map. */
const PATIENT_FIELDS = [
    'first-name',
    'last-name',
    'full-name',
    'dob',
    'gender',
    'email',
    'phone',
    'address-line-1',
    'address-line-2',
    'city',
    'state',
    'postal-code',
    'country',
];
/**
 * Fields exposed for kind=fhir-coverage. Matches the dispatcher's
 * accepted field names in packages/validation/src/prefill.ts:fillCoverage.
 */
const COVERAGE_FIELDS = ['payor', 'subscriberId', 'dependent', 'group', 'relationship'];
const CODE_KINDS = new Set(['observation', 'condition', 'medication']);
const DEFAULT_SYSTEM = {
    observation: 'http://loinc.org',
    condition: 'http://snomed.info/sct',
    medication: 'http://www.nlm.nih.gov/research/umls/rxnorm',
};
function isCodeKind(kind) {
    return CODE_KINDS.has(kind);
}
function getPrefillSource(item) {
    const ext = item.extension?.find((e) => e.url === ExtensionUrl.prefillSource);
    const raw = ext?.valueString;
    if (typeof raw !== 'string' || raw.trim() === '')
        return EMPTY_SOURCE;
    try {
        const parsed = JSON.parse(raw);
        const kind = parsed['kind'];
        if (kind === 'patient') {
            return {
                kind: 'patient',
                field: typeof parsed['field'] === 'string' ? parsed['field'] : '',
                system: '',
                code: '',
            };
        }
        if (kind === 'fhir-coverage') {
            return {
                kind: 'fhir-coverage',
                field: typeof parsed['field'] === 'string' ? parsed['field'] : '',
                system: '',
                code: '',
            };
        }
        if (kind === 'observation' || kind === 'condition' || kind === 'medication') {
            return {
                kind,
                field: '',
                system: typeof parsed['system'] === 'string' ? parsed['system'] : '',
                code: typeof parsed['code'] === 'string' ? parsed['code'] : '',
            };
        }
    }
    catch {
        // fall through — malformed JSON resets to none
    }
    return EMPTY_SOURCE;
}
function setPrefillSource(item, state) {
    const without = (item.extension || []).filter((e) => e.url !== ExtensionUrl.prefillSource);
    const payload = serializePrefillSource(state);
    const next = payload === undefined
        ? without
        : [...without, { url: ExtensionUrl.prefillSource, valueString: payload }];
    return { ...item, extension: next.length > 0 ? next : undefined };
}
/**
 * Serialise UI state to JSON. `none` removes the extension entirely;
 * every other kind serialises whatever the clinician has typed so far,
 * even if the source is incomplete. We keep this permissive on purpose:
 *  - The drawer needs the saved state to round-trip across re-renders
 *    so the conditional sub-fields stay visible while the clinician
 *    fills them in.
 *  - The runtime engine treats malformed / incomplete sources as
 *    "no source" and falls back to the linkId convention map (see
 *    `readPrefillSource` in @intakeform/validation). So an incomplete
 *    save is forgiving at runtime, not an error.
 */
function serializePrefillSource(state) {
    switch (state.kind) {
        case 'none':
            return undefined;
        case 'patient':
            return JSON.stringify({
                kind: 'patient',
                ...(state.field === '' ? {} : { field: state.field }),
            });
        case 'fhir-coverage':
            return JSON.stringify({
                kind: 'fhir-coverage',
                ...(state.field === '' ? {} : { field: state.field }),
            });
        case 'observation':
        case 'condition':
        case 'medication':
            return JSON.stringify({
                kind: state.kind,
                ...(state.system === '' ? {} : { system: state.system }),
                ...(state.code === '' ? {} : { code: state.code }),
            });
        default: {
            const _exhaustive = state.kind;
            return _exhaustive;
        }
    }
}
const TEXT_FORMAT_URLS = [
    ExtensionUrl.emailQuality,
    ExtensionUrl.phoneValidation,
    ExtensionUrl.address,
];
function readTextFormat(item) {
    const exts = item.extension ?? [];
    if (exts.some((e) => e.url === ExtensionUrl.address && e.valueBoolean === true))
        return 'address';
    if (exts.some((e) => e.url === ExtensionUrl.phoneValidation && e.valueBoolean === true)) {
        return 'phone';
    }
    if (exts.some((e) => e.url === ExtensionUrl.emailQuality && e.valueBoolean === true)) {
        return 'email';
    }
    return 'none';
}
function writeTextFormat(item, format) {
    // Strip any of the three format extensions first so they stay
    // mutually exclusive. Other extensions (helpText, prefill source,
    // insurance-card-target, etc.) are preserved.
    const others = (item.extension ?? []).filter((e) => !TEXT_FORMAT_URLS.includes(e.url));
    let next = others;
    if (format === 'email') {
        next = [...others, { url: ExtensionUrl.emailQuality, valueBoolean: true }];
    }
    else if (format === 'phone') {
        next = [...others, { url: ExtensionUrl.phoneValidation, valueBoolean: true }];
    }
    else if (format === 'address') {
        next = [...others, { url: ExtensionUrl.address, valueBoolean: true }];
    }
    return { ...item, extension: next.length > 0 ? next : undefined };
}
function TextFormatPicker({ item, onChange }) {
    const formatId = useId();
    const value = readTextFormat(item);
    return (_jsxs("details", { open: true, className: "field-config-section", children: [_jsx("summary", { className: "field-config-section-title", children: "Format" }), _jsx("div", { className: "field-config-section-body", children: _jsxs(Stack, { gap: 4, children: [_jsx(Label, { htmlFor: formatId, children: "Validation / widget" }), _jsxs("select", { id: formatId, "data-testid": "text-format-picker", className: "field-config-select", value: value, onChange: (e) => onChange(writeTextFormat(item, e.currentTarget.value)), children: [_jsx("option", { value: "none", children: "None \u2014 free text" }), _jsx("option", { value: "email", children: "Email \u2014 verify deliverability + reject disposable" }), _jsx("option", { value: "phone", children: "Phone \u2014 parse + validate (libphonenumber)" }), _jsx("option", { value: "address", children: "Address \u2014 autocomplete (Google Places)" })] })] }) })] }));
}
/* -------------------------------------------------------------------- */
/*  "Insurance card target" section — bead 2pc                           */
/* -------------------------------------------------------------------- */
const INSURANCE_CARD_TARGET_FIELDS = [
    { value: '', label: '— none —' },
    { value: 'payor', label: 'Payor / plan name' },
    { value: 'memberId', label: 'Member id' },
    { value: 'groupNumber', label: 'Group number' },
];
function InsuranceCardTargetSection({ item, onChange }) {
    const targetId = useId();
    const current = item.extension?.find((e) => e.url === ExtensionUrl.insuranceCardTarget);
    const value = typeof current?.valueCode === 'string' ? current.valueCode : '';
    // Only render the panel when SOMETHING in the form has the card
    // marker — but we don't have allItems here. Keep the panel open
    // for any string/text field; clinicians who don't use insurance
    // cards just leave it on "none."
    return (_jsxs("details", { className: "field-config-section", "data-insurance-card-section": "true", children: [_jsx("summary", { className: "field-config-section-title", children: "Fill from insurance-card OCR" }), _jsx("div", { className: "field-config-section-body", children: _jsxs(Stack, { gap: 12, children: [_jsx("p", { className: "muted", style: { margin: 0 }, children: "When the patient uploads an insurance-card image earlier in this form, populate this item from the OCR-parsed field below." }), _jsxs(Stack, { gap: 4, children: [_jsx(Label, { htmlFor: targetId, children: "Card field" }), _jsx("select", { id: targetId, className: "field-config-select", value: value, "data-insurance-card-target": true, onChange: (e) => {
                                        const next = e.currentTarget.value;
                                        const others = (item.extension ?? []).filter((x) => x.url !== ExtensionUrl.insuranceCardTarget);
                                        if (next === '') {
                                            onChange({
                                                ...item,
                                                extension: others.length > 0 ? others : undefined,
                                            });
                                        }
                                        else {
                                            onChange({
                                                ...item,
                                                extension: [
                                                    ...others,
                                                    { url: ExtensionUrl.insuranceCardTarget, valueCode: next },
                                                ],
                                            });
                                        }
                                    }, children: INSURANCE_CARD_TARGET_FIELDS.map((f) => (_jsx("option", { value: f.value, children: f.label }, f.value))) })] })] }) })] }));
}
function EnableWhenSection({ item, onChange, allItems }) {
    const behaviorId = useId();
    const conditions = item.enableWhen ?? [];
    const eligibleGates = getEligibleGates(allItems, item.linkId);
    const behavior = item.enableBehavior ?? 'any';
    // Hide the entire panel if there's nothing to gate on AND no existing
    // rules (avoids a useless empty section on the first item in a form).
    // Keep it visible if there are existing rules — even if they're
    // pointing at an item that no longer exists, the clinician still
    // needs to be able to see + remove them.
    if (eligibleGates.length === 0 && conditions.length === 0)
        return null;
    const update = (next, nextBehavior) => {
        const patch = { ...item };
        if (next.length === 0) {
            delete patch.enableWhen;
            delete patch.enableBehavior;
        }
        else {
            patch.enableWhen = [...next];
            // enableBehavior is only meaningful with >1 condition; keep the
            // FHIR payload tidy by omitting the default ('any') when there's
            // a single clause.
            if (next.length > 1)
                patch.enableBehavior = nextBehavior ?? behavior;
            else
                delete patch.enableBehavior;
        }
        onChange(patch);
    };
    const handleAdd = () => {
        update([...conditions, makeStarterClause(eligibleGates[0])]);
    };
    return (_jsxs("details", { className: "field-config-section", "data-enable-when-section": "true", children: [_jsxs("summary", { className: "field-config-section-title", children: ['Show this item when…', conditions.length > 0 ? (_jsxs("span", { className: "field-config-section-count", "data-enable-when-count": true, children: [' ', "(", conditions.length, ")"] })) : null] }), _jsx("div", { className: "field-config-section-body", children: _jsxs(Stack, { gap: 12, children: [conditions.length === 0 ? (_jsx("p", { className: "muted", style: { margin: 0 }, children: "This item is always shown. Add a condition to hide it until an earlier answer matches." })) : (conditions.map((clause, idx) => (_jsx(EnableWhenRow
                        // The clause has no stable id — use the index. Reordering
                        // would warrant a real key, but we don't expose reorder
                        // yet; if/when we do, generate UUIDs at clause creation.
                        , { clause: clause, eligibleGates: eligibleGates, onChange: (nextClause) => {
                                const next = [...conditions];
                                next[idx] = nextClause;
                                update(next);
                            }, onRemove: () => {
                                const next = conditions.filter((_, i) => i !== idx);
                                update(next);
                            } }, idx)))), eligibleGates.length > 0 && (_jsx("div", { children: _jsx(Button, { variant: "ghost", type: "button", onClick: handleAdd, "data-add-enable-when": true, children: '+ Add condition' }) })), conditions.length > 1 && (_jsxs(Stack, { gap: 4, children: [_jsx(Label, { htmlFor: behaviorId, children: "Combine conditions with" }), _jsxs("select", { id: behaviorId, className: "field-config-select", value: behavior, "data-enable-when-behavior": true, onChange: (e) => {
                                        update(conditions, e.currentTarget.value === 'all' ? 'all' : 'any');
                                    }, children: [_jsx("option", { value: "any", children: 'Any (OR — show if at least one matches)' }), _jsx("option", { value: "all", children: 'All (AND — show only if every condition matches)' })] })] }))] }) })] }));
}
function EnableWhenRow({ clause, eligibleGates, onChange, onRemove }) {
    const sourceId = useId();
    const operatorId = useId();
    const valueId = useId();
    const source = eligibleGates.find((g) => g.linkId === clause.question);
    const sourceType = source?.type;
    const sourceOptions = source?.answerOption;
    const handleSourceChange = (linkId) => {
        const newSource = eligibleGates.find((g) => g.linkId === linkId);
        if (!newSource)
            return;
        // Wipe the previous answer key — its shape is wrong for the new
        // source type. The user can re-enter the value below.
        const next = clearAnswerValue({ ...clause, question: linkId });
        if (clause.operator === 'exists') {
            next.answerBoolean = true;
        }
        onChange(next);
    };
    const handleOperatorChange = (operator) => {
        const next = clearAnswerValue({ ...clause, operator });
        if (operator === 'exists') {
            next.answerBoolean = true;
        }
        else if (sourceType === 'choice' && sourceOptions !== undefined && sourceOptions.length > 0) {
            // Choice gates seed to the first answerOption — the value key
            // and shape depend on the option (valueCoding vs valueString
            // vs valueInteger), so route through the variant resolver.
            const first = sourceOptions[0];
            const variant = first === undefined ? null : answerKeyForChoiceOption(first);
            if (variant !== null) {
                next[variant.key] = variant.value;
            }
        }
        else if (sourceType !== undefined) {
            // Re-seed with a typed default so the input field has something
            // sensible to bind to.
            const key = answerKeyForType(sourceType);
            switch (key) {
                case 'answerBoolean':
                    next.answerBoolean = true;
                    break;
                case 'answerInteger':
                    next.answerInteger = 0;
                    break;
                case 'answerDecimal':
                    next.answerDecimal = 0;
                    break;
                default:
                    // string/date/time/dateTime — empty string is safe.
                    next[key] = '';
                    break;
            }
        }
        onChange(next);
    };
    return (_jsx("div", { className: "enable-when-row", "data-enable-when-row": true, children: _jsxs(Stack, { gap: 6, children: [_jsxs(Stack, { gap: 4, children: [_jsx(Label, { htmlFor: sourceId, children: "When" }), _jsxs("select", { id: sourceId, className: "field-config-select", value: clause.question, "data-enable-when-source": true, onChange: (e) => handleSourceChange(e.currentTarget.value), children: [source === undefined && clause.question !== '' && (_jsx("option", { value: clause.question, children: `(missing: ${clause.question})` })), eligibleGates.map((g) => (_jsx("option", { value: g.linkId, children: g.text ?? g.linkId }, g.linkId)))] })] }), _jsxs(Stack, { gap: 4, children: [_jsx(Label, { htmlFor: operatorId, children: '' }), _jsx("select", { id: operatorId, className: "field-config-select", value: clause.operator, "data-enable-when-operator": true, onChange: (e) => handleOperatorChange(e.currentTarget.value), children: ENABLE_WHEN_OPERATORS.map((op) => (_jsx("option", { value: op.value, children: op.label }, op.value))) })] }), clause.operator !== 'exists' && sourceType !== undefined && (_jsxs(Stack, { gap: 4, children: [_jsx(Label, { htmlFor: valueId, children: "Value" }), _jsx(EnableWhenValueInput, { id: valueId, clause: clause, sourceType: sourceType, sourceOptions: sourceOptions, onChange: onChange })] })), _jsx("div", { children: _jsx(Button, { variant: "danger", type: "button", onClick: onRemove, "data-remove-enable-when": true, children: 'Remove condition' }) })] }) }));
}
function EnableWhenValueInput({ id, clause, sourceType, sourceOptions, onChange, }) {
    const setKey = (key, value) => {
        const next = clearAnswerValue(clause);
        next[key] = value;
        onChange(next);
    };
    switch (sourceType) {
        case 'boolean':
            return (_jsxs("select", { id: id, className: "field-config-select", value: clause.answerBoolean === true ? 'true' : 'false', "data-enable-when-value": true, onChange: (e) => setKey('answerBoolean', e.currentTarget.value === 'true'), children: [_jsx("option", { value: "true", children: 'Yes' }), _jsx("option", { value: "false", children: 'No' })] }));
        case 'integer':
            return (_jsx(Input, { id: id, type: "number", step: "1", value: clause.answerInteger ?? 0, "data-enable-when-value": true, onChange: (e) => setKey('answerInteger', Number.parseInt(e.currentTarget.value, 10) || 0) }));
        case 'decimal':
            return (_jsx(Input, { id: id, type: "number", step: "any", value: clause.answerDecimal ?? 0, "data-enable-when-value": true, onChange: (e) => setKey('answerDecimal', Number.parseFloat(e.currentTarget.value) || 0) }));
        case 'date':
            return (_jsx(Input, { id: id, type: "date", value: clause.answerDate ?? '', "data-enable-when-value": true, onChange: (e) => setKey('answerDate', e.currentTarget.value) }));
        case 'dateTime':
            return (_jsx(Input, { id: id, type: "datetime-local", value: clause.answerDateTime ?? '', "data-enable-when-value": true, onChange: (e) => setKey('answerDateTime', e.currentTarget.value) }));
        case 'time':
            return (_jsx(Input, { id: id, type: "time", value: clause.answerTime ?? '', "data-enable-when-value": true, onChange: (e) => setKey('answerTime', e.currentTarget.value) }));
        case 'choice': {
            const options = sourceOptions ?? [];
            // Resolve which option the clause currently points at. The clause
            // may have `answerCoding`, `answerString`, or `answerInteger` —
            // match by the same projection the renderer uses (the `.code`
            // for codings, the literal for primitives).
            const currentKey = (() => {
                if (clause.answerCoding !== undefined) {
                    const c = clause.answerCoding;
                    return `c:${c.system ?? ''}|${c.code ?? ''}`;
                }
                if (typeof clause.answerString === 'string')
                    return `s:${clause.answerString}`;
                if (typeof clause.answerInteger === 'number')
                    return `i:${String(clause.answerInteger)}`;
                return '';
            })();
            return (_jsx("select", { id: id, className: "field-config-select", value: currentKey, "data-enable-when-value": true, onChange: (e) => {
                    const selectedKey = e.currentTarget.value;
                    const picked = options.find((o) => optionKey(o) === selectedKey);
                    if (picked === undefined)
                        return;
                    const variant = answerKeyForChoiceOption(picked);
                    if (variant === null)
                        return;
                    setKey(variant.key, variant.value);
                }, children: options.map((o) => {
                    const k = optionKey(o);
                    return (_jsx("option", { value: k, children: optionLabel(o) }, k));
                }) }));
        }
        case 'string':
        case 'text':
        default:
            return (_jsx(Input, { id: id, type: "text", value: clause.answerString ?? '', "data-enable-when-value": true, onChange: (e) => setKey('answerString', e.currentTarget.value) }));
    }
}
/** Labeled select bound to source.field. Used for patient + coverage. */
function PrefillFieldPicker({ source, fields, label, testId, onChange }) {
    const fieldId = useId();
    return (_jsxs(Stack, { gap: 4, children: [_jsx(Label, { htmlFor: fieldId, children: label }), _jsxs("select", { id: fieldId, "data-testid": testId, className: "field-config-select", value: source.field, onChange: (e) => onChange({ ...source, field: e.currentTarget.value }), children: [_jsx("option", { value: "", children: "\u2014 pick a field \u2014" }), fields.map((f) => (_jsx("option", { value: f, children: f }, f)))] })] }));
}
/** System-URI + Code inputs used for observation / condition / medication. */
function PrefillCodeFields({ source, onChange }) {
    const systemId = useId();
    const codeId = useId();
    const placeholder = source.kind === 'observation'
        ? '8302-2 (body height)'
        : source.kind === 'condition'
            ? '73211009 (diabetes mellitus)'
            : 'leave blank to list all active meds';
    return (_jsxs(_Fragment, { children: [_jsxs(Stack, { gap: 4, children: [_jsx(Label, { htmlFor: systemId, children: "Code system URI" }), _jsx(Input, { id: systemId, "data-testid": "prefill-source-system", type: "text", value: source.system, onChange: (e) => onChange({ ...source, system: e.currentTarget.value }) })] }), _jsxs(Stack, { gap: 4, children: [_jsxs(Label, { htmlFor: codeId, children: ["Code", source.kind === 'medication' ? ' (optional)' : ''] }), _jsx(Input, { id: codeId, "data-testid": "prefill-source-code", type: "text", placeholder: placeholder, value: source.code, onChange: (e) => onChange({ ...source, code: e.currentTarget.value }) })] })] }));
}
/**
 * The "Prefill from EMR" section. Self-contained: owns its kind
 * selector and dispatches to the kind-specific sub-components above.
 * Pulled out of the main drawer to keep FieldConfigDrawer's render
 * scannable + to make the section testable in isolation.
 */
function PrefillFromEMRSection({ item, onChange }) {
    const kindId = useId();
    const source = getPrefillSource(item);
    const apply = (next) => {
        onChange(setPrefillSource(item, next));
    };
    return (_jsxs("details", { className: "field-config-section", "data-prefill-section": "true", children: [_jsx("summary", { className: "field-config-section-title", children: "Prefill from EMR" }), _jsx("div", { className: "field-config-section-body", children: _jsxs(Stack, { gap: 12, children: [_jsxs(Stack, { gap: 4, children: [_jsx(Label, { htmlFor: kindId, children: "Source" }), _jsxs("select", { id: kindId, "data-testid": "prefill-source-kind", className: "field-config-select", value: source.kind, onChange: (e) => {
                                        const kind = e.currentTarget.value;
                                        // Switching kind: reset the now-irrelevant fields,
                                        // pre-fill the default coding system for code-based
                                        // kinds so the clinician doesn't have to type the
                                        // LOINC URI.
                                        let next;
                                        if (kind === 'none')
                                            next = EMPTY_SOURCE;
                                        else if (kind === 'patient' || kind === 'fhir-coverage') {
                                            next = {
                                                kind,
                                                field: source.kind === kind ? source.field : '',
                                                system: '',
                                                code: '',
                                            };
                                        }
                                        else if (isCodeKind(kind)) {
                                            next = {
                                                kind,
                                                field: '',
                                                system: source.kind === kind && source.system !== ''
                                                    ? source.system
                                                    : DEFAULT_SYSTEM[kind],
                                                code: source.kind === kind ? source.code : '',
                                            };
                                        }
                                        else {
                                            next = EMPTY_SOURCE;
                                        }
                                        apply(next);
                                    }, children: [_jsx("option", { value: "none", children: "No automatic prefill" }), _jsx("option", { value: "patient", children: "Patient demographics" }), _jsx("option", { value: "fhir-coverage", children: "FHIR Coverage (insurance)" }), _jsx("option", { value: "observation", children: "Observation (LOINC)" }), _jsx("option", { value: "condition", children: "Condition (SNOMED)" }), _jsx("option", { value: "medication", children: "Medication (RxNorm)" })] })] }), source.kind === 'patient' && (_jsx(PrefillFieldPicker, { source: source, fields: PATIENT_FIELDS, label: "Demographic field", testId: "prefill-source-field", onChange: apply })), source.kind === 'fhir-coverage' && (_jsx(PrefillFieldPicker, { source: source, fields: COVERAGE_FIELDS, label: "Coverage field", testId: "prefill-coverage-field", onChange: apply })), isCodeKind(source.kind) && _jsx(PrefillCodeFields, { source: source, onChange: apply })] }) })] }));
}
export function FieldConfigDrawer({ item, onChange, allItems }) {
    const textId = useId();
    const helpTextId = useId();
    const maxLenId = useId();
    const requiredId = useId();
    const countryBiasId = useId();
    if (item === undefined) {
        return (_jsxs("div", { className: "field-config-empty-state", children: [_jsx("span", { className: "field-config-empty-icon", children: _jsx(SlidersHorizontal, { size: 22, strokeWidth: 1.4, "aria-hidden": "true" }) }), _jsx("p", { children: "Select a section header or a field on the canvas to configure it." })] }));
    }
    const isGroup = item.type === 'group';
    return (_jsxs("div", { className: "field-config-drawer-container", children: [_jsxs("div", { className: "field-config-header", children: [_jsx("span", { className: "field-config-item-badge", children: item.type }), _jsxs("span", { className: "field-config-item-linkid", children: ["ID: ", item.linkId] })] }), _jsxs("details", { open: true, className: "field-config-section", children: [_jsx("summary", { className: "field-config-section-title", children: "General Settings" }), _jsx("div", { className: "field-config-section-body", children: _jsxs(Stack, { gap: 12, children: [_jsxs(Stack, { gap: 4, children: [_jsx(Label, { htmlFor: textId, children: isGroup ? 'Section Title' : 'Field Label' }), _jsx(Input, { id: textId, type: "text", value: item.text ?? '', onChange: (e) => {
                                                const next = e.currentTarget.value;
                                                onChange({ ...item, text: next === '' ? undefined : next });
                                            } })] }), !isGroup && (_jsxs(_Fragment, { children: [_jsxs(Stack, { gap: 4, children: [_jsx(Label, { htmlFor: helpTextId, children: 'Help Text (Why we ask)' }), _jsx("textarea", { id: helpTextId, className: "field-config-textarea", placeholder: "Provide patient context or legal explanation...", value: getHelpText(item), onChange: (e) => {
                                                        onChange(setHelpText(item, e.currentTarget.value));
                                                    } })] }), _jsxs("div", { className: "field-config-checkbox-row", children: [_jsx("input", { id: requiredId, type: "checkbox", checked: item.required === true, onChange: (e) => {
                                                        onChange({ ...item, required: e.currentTarget.checked });
                                                    } }), _jsx(Label, { htmlFor: requiredId, children: 'Required Field' })] })] }))] }) })] }), allItems !== undefined && (_jsx(EnableWhenSection, { item: item, onChange: onChange, allItems: allItems })), item.type === 'string' && _jsx(TextFormatPicker, { item: item, onChange: onChange }), (item.type === 'string' || item.type === 'text') && (_jsx(InsuranceCardTargetSection, { item: item, onChange: onChange })), !isGroup && _jsx(PrefillFromEMRSection, { item: item, onChange: onChange }), !isGroup && (item.type === 'string' || item.type === 'text') && (_jsxs("details", { className: "field-config-section", children: [_jsx("summary", { className: "field-config-section-title", children: "Validation Settings" }), _jsx("div", { className: "field-config-section-body", children: _jsxs(Stack, { gap: 12, children: [_jsxs(Stack, { gap: 4, children: [_jsx(Label, { htmlFor: maxLenId, children: 'Max length' }), _jsx(Input, { id: maxLenId, type: "number", min: 1, value: item.maxLength === undefined ? '' : String(item.maxLength), onChange: (e) => {
                                                const raw = e.currentTarget.value;
                                                if (raw === '') {
                                                    const { maxLength: _maxLength, ...rest } = item;
                                                    onChange(rest);
                                                    return;
                                                }
                                                const n = Number(raw);
                                                if (Number.isInteger(n) && n > 0)
                                                    onChange({ ...item, maxLength: n });
                                            } })] }), isAddressItem(item) ? (_jsxs(Stack, { gap: 4, children: [_jsx(Label, { htmlFor: countryBiasId, children: 'Country bias' }), _jsx("select", { id: countryBiasId, value: readCountryBias(item), onChange: (e) => onChange(writeCountryBias(item, e.currentTarget.value)), children: ADDRESS_COUNTRY_OPTIONS.map((opt) => (_jsx("option", { value: opt.code, children: opt.label }, opt.code))) })] })) : null] }) })] })), !isGroup && item.type === 'choice' && (_jsxs("details", { open: true, className: "field-config-section", children: [_jsx("summary", { className: "field-config-section-title", children: "Answer Options" }), _jsx("div", { className: "field-config-section-body", children: _jsxs(Stack, { gap: 8, children: [_jsx("div", { className: "options-editor-list", children: (item.answerOption || []).map((opt, idx) => (_jsxs("div", { className: "option-editor-row", children: [_jsx("input", { type: "text", className: "option-editor-input", value: opt.valueCoding?.display ?? opt.valueString ?? '', onChange: (e) => {
                                                    const nextOptions = [...(item.answerOption || [])];
                                                    const label = e.currentTarget.value;
                                                    // Keep the option's extensions (e.g. its scoring
                                                    // `ordinalValue` weight) when only the label changes;
                                                    // a coded option keeps its code and changes only its
                                                    // display, so answers already stored still match it.
                                                    nextOptions[idx] = {
                                                        ...(opt.valueCoding !== undefined
                                                            ? { valueCoding: { ...opt.valueCoding, display: label } }
                                                            : { valueString: label }),
                                                        ...(opt.extension !== undefined ? { extension: opt.extension } : {}),
                                                    };
                                                    onChange({ ...item, answerOption: nextOptions });
                                                } }), _jsxs("div", { className: "option-editor-actions", children: [_jsx(Button, { variant: "ghost", type: "button", onClick: () => {
                                                            const nextOptions = [...(item.answerOption ?? [])];
                                                            const here = nextOptions[idx];
                                                            const prev = nextOptions[idx - 1];
                                                            if (idx > 0 && here !== undefined && prev !== undefined) {
                                                                nextOptions[idx] = prev;
                                                                nextOptions[idx - 1] = here;
                                                                onChange({ ...item, answerOption: nextOptions });
                                                            }
                                                        }, disabled: idx === 0, children: '↑' }), _jsx(Button, { variant: "ghost", type: "button", onClick: () => {
                                                            const nextOptions = [...(item.answerOption ?? [])];
                                                            const here = nextOptions[idx];
                                                            const next = nextOptions[idx + 1];
                                                            if (idx < nextOptions.length - 1 &&
                                                                here !== undefined &&
                                                                next !== undefined) {
                                                                nextOptions[idx] = next;
                                                                nextOptions[idx + 1] = here;
                                                                onChange({ ...item, answerOption: nextOptions });
                                                            }
                                                        }, disabled: idx === (item.answerOption ?? []).length - 1, children: '↓' }), _jsx(Button, { variant: "danger", type: "button", onClick: () => {
                                                            const nextOptions = (item.answerOption || []).filter((_, i) => i !== idx);
                                                            onChange({ ...item, answerOption: nextOptions });
                                                        }, children: '×' })] })] }, idx))) }), _jsx("button", { type: "button", className: "add-option-btn", onClick: () => {
                                        const nextOptions = [
                                            ...(item.answerOption || []),
                                            { valueString: `Option ${(item.answerOption || []).length + 1}` },
                                        ];
                                        onChange({ ...item, answerOption: nextOptions });
                                    }, children: '+ Add Choice Option' })] }) })] }))] }));
}
//# sourceMappingURL=FieldConfigDrawer.js.map